import PropTypes from 'prop-types'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Divider, Flex, Grid, Typography } from 'antd'
import { AUTH_TOKEN, MAKE_ID } from '../../constants/common'
import { setLocalStorageData } from '../../utils/LocalStorageAccess'
import { clearHostedLoginBreadcrumb, isExternalUrl } from '../../utils/hostedLogin'
import {
  answerNewPassword,
  cognitoSignIn,
  confirmPasswordReset,
  getProviderRedirect,
  requestPasswordReset,
} from './api'
import { reasonMessage, safeNextPath } from './reasons'
import SignInForm from './SignInForm'
import NewPasswordForm from './NewPasswordForm'
import { ConfirmResetForm, RequestResetForm } from './ForgotPasswordForm'
import ProviderButton from './ProviderButton'
import LoginCard from './LoginCard'

const { Text } = Typography

const SIGN_IN = 'signIn'
const NEW_PASSWORD = 'newPassword'
const RESET_REQUEST = 'resetRequest'
const RESET_CONFIRM = 'resetConfirm'

// Title and prompt for each step after sign in (same wording roles as Tracer)
const STEP_TEXT = {
  [SIGN_IN]: { title: 'auth.signin', prompt: 'auth.loginPrompt' },
  [NEW_PASSWORD]: { title: 'auth.newPasswordTitle', prompt: 'auth.newPasswordPrompt' },
  [RESET_REQUEST]: { title: 'auth.resetPasswordTitle', prompt: 'auth.forgotPasswordPrompt' },
  [RESET_CONFIRM]: { title: 'auth.resetPasswordTitle', prompt: 'auth.resetCodeSent' },
}

/**
 * Sign-in for a host whose users live in a Cognito user pool, laid out like
 * Tracer's login page: the email form and the identity provider buttons side by
 * side (stacked on narrow screens), errors inline in the card. Credentials go
 * to AT's backend, which talks to Cognito; nothing here touches Cognito itself.
 */
const CognitoLogin = ({ providers, initialNotice = null }) => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const screens = Grid.useBreakpoint()
  const [step, setStep] = useState(SIGN_IN)
  const [email, setEmail] = useState('')
  const [challengeId, setChallengeId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [busyProvider, setBusyProvider] = useState(null)
  // { type: 'error' | 'success', message } and an i18n id, shown inline in the card
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(initialNotice)

  const goTo = (nextStep) => {
    setError(null)
    setNotice(null)
    if (nextStep === SIGN_IN) setChallengeId(null)
    setStep(nextStep)
  }

  const showError = (res) => setError({ type: 'error', message: reasonMessage(intl, res) })

  const withBusy = async (call) => {
    setBusy(true)
    setError(null)
    try {
      return await call()
    } finally {
      setBusy(false)
    }
  }

  // Every Cognito call answers with one of: signed in, a challenge to answer,
  // or a reason it failed. This turns that into the page's next step.
  const handleResult = (res) => {
    if (res?.result === 'success') {
      clearHostedLoginBreadcrumb()
      setError({ type: 'success', message: intl.formatMessage({ id: 'auth.loginSuccess' }) })
      setLocalStorageData(AUTH_TOKEN, res?.token)
      setLocalStorageData(MAKE_ID, res?.make)
      // A full load, not a route change: the layout reads the token once on mount.
      setBusy(true)
      window.location.href = safeNextPath(searchParams.get('next'))
      return
    }
    if (res?.result === 'challenge') {
      setChallengeId(res?.challenge_id || null)
      goTo(NEW_PASSWORD)
      return
    }
    if (res?.reason === 'password_reset_required') {
      // An administrator reset the password, and Cognito has already sent a code
      goTo(RESET_CONFIRM)
      setNotice('auth.passwordResetRequired')
      return
    }
    if (res?.reason === 'challenge_expired') {
      goTo(SIGN_IN)
    }
    showError(res)
  }

  const signIn = async (values) => {
    setEmail(values.email)
    handleResult(await withBusy(() => cognitoSignIn(values.email, values.password)))
  }

  const setNewPassword = async (newPassword) => {
    handleResult(await withBusy(() => answerNewPassword(challengeId, newPassword)))
  }

  const sendResetCode = async (address) => {
    const res = await withBusy(() => requestPasswordReset(address))
    if (res?.result === 'code_sent') {
      setEmail(address)
      return true
    }
    showError(res)
    return false
  }

  const requestReset = async (address) => {
    if (await sendResetCode(address)) goTo(RESET_CONFIRM)
  }

  const resendCode = async () => {
    if (await sendResetCode(email)) setNotice('auth.resetCodeResent')
  }

  const confirmReset = async (code, newPassword) => {
    handleResult(await withBusy(() => confirmPasswordReset(email, code, newPassword)))
  }

  const selectProvider = async (provider) => {
    setBusyProvider(provider)
    setError(null)
    const res = await getProviderRedirect(provider)
    // An explicit click, so no repeat-window check - but never "to the provider"
    // on our own origin, which would only loop back here
    if (res?.redirect_url && isExternalUrl(res.redirect_url)) {
      window.location.href = res.redirect_url
      return
    }
    setBusyProvider(null)
    showError(res)
  }

  const renderStep = () => {
    switch (step) {
      case NEW_PASSWORD:
        return <NewPasswordForm busy={busy} onSubmit={setNewPassword} onBack={() => goTo(SIGN_IN)} />
      case RESET_REQUEST:
        return (
          <RequestResetForm busy={busy} initialEmail={email} onSubmit={requestReset} onBack={() => goTo(SIGN_IN)} />
        )
      case RESET_CONFIRM:
        return (
          <ConfirmResetForm busy={busy} onSubmit={confirmReset} onResend={resendCode} onBack={() => goTo(SIGN_IN)} />
        )
      default:
        return (
          <SignInForm
            busy={busy || !!busyProvider}
            initialEmail={email}
            onSubmit={signIn}
            onForgotPassword={(typed) => {
              if (typed) setEmail(typed)
              goTo(RESET_REQUEST)
            }}
          />
        )
    }
  }

  const hasProviders = providers?.length > 0 && step === SIGN_IN
  // Side by side from the md breakpoint up; stacked on narrower screens
  const sideBySide = hasProviders && screens.md !== false
  const stepText = STEP_TEXT[step]

  return (
    <LoginCard
      title={intl.formatMessage({ id: stepText.title })}
      prompt={intl.formatMessage({ id: stepText.prompt })}
      notice={notice && intl.formatMessage({ id: notice })}
      error={error}
      wide={hasProviders}
    >
      {({ alerts }) => (
        <Flex
          vertical={!sideBySide}
          gap="large"
          className={`login-columns${sideBySide ? ' side-by-side' : ''}`}
          data-testid="login-columns"
          data-layout={sideBySide ? 'side-by-side' : 'stacked'}
        >
          <Flex vertical={true} gap="middle" className="login-column">
            {alerts}
            {renderStep()}
          </Flex>

          {hasProviders &&
            (sideBySide ? (
              // antd's vertical Divider cannot carry text, so the side-by-side
              // rule is drawn here; stacked, antd's own Divider is used.
              <div className="login-divider" role="separator">
                <Text type="secondary">{intl.formatMessage({ id: 'auth.orContinueWith' })}</Text>
              </div>
            ) : (
              <Divider plain style={{ margin: 0 }}>
                {intl.formatMessage({ id: 'auth.orContinueWith' })}
              </Divider>
            ))}

          {hasProviders && (
            <Flex vertical={true} gap="middle" justify="center" className="login-providers">
              {providers.map((provider) => (
                <ProviderButton
                  key={provider.name}
                  provider={provider}
                  busy={busy || !!busyProvider}
                  loading={busyProvider === provider.name}
                  onSelect={selectProvider}
                />
              ))}
            </Flex>
          )}
        </Flex>
      )}
    </LoginCard>
  )
}

CognitoLogin.propTypes = {
  providers: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, label: PropTypes.string, logo_url: PropTypes.string })
  ),
  // An i18n id shown as the page opens, e.g. after logout
  initialNotice: PropTypes.string,
}

export default CognitoLogin
